import { useCallback, useEffect, useRef, useState } from 'react'
import apiService from '@/services/api'
import { ChatStreamBody, ChatStreamError } from '@/services/chatStream'
import { ChatSource } from '@/types'

export const CHAT_STREAMING_ENABLED = import.meta.env.VITE_CHAT_STREAMING !== 'false'

export interface ChatStreamCallbacks {
  /** Accumulated answer text so far; batched to at most one call per animation frame. */
  onText: (text: string) => void
  onSources?: (sources: ChatSource[]) => void
  onSuggestions?: (questions: string[]) => void
  /** The final, fully cleaned answer; replaces whatever onText showed. */
  onDone?: (answer: string) => void
}

export type ChatStreamResult =
  | { status: 'done'; text: string }
  /** The user pressed Stop; `text` is what had arrived. */
  | { status: 'stopped'; text: string }
  /** Cancelled because the chat closed or switched context; ignore it. */
  | { status: 'aborted'; text: string }
  | { status: 'error'; text: string; error: ChatStreamError }

/**
 * Sends a chat question and streams the answer back.
 *
 * Owns the in-flight request: a new send, stop(), abort() or unmounting cancels it.
 * With VITE_CHAT_STREAMING=false it falls back to the one-shot JSON endpoint and
 * reports the answer through the same callbacks.
 */
export function useChatStream() {
  const [isStreaming, setIsStreaming] = useState(false)
  const controllerRef = useRef<AbortController | null>(null)
  const stopReasonRef = useRef<'stopped' | 'aborted' | null>(null)

  useEffect(() => () => controllerRef.current?.abort(), [])

  const cancel = useCallback((reason: 'stopped' | 'aborted') => {
    if (controllerRef.current) {
      stopReasonRef.current = reason
      controllerRef.current.abort()
    }
  }, [])

  const stop = useCallback(() => cancel('stopped'), [cancel])
  const abort = useCallback(() => cancel('aborted'), [cancel])

  const send = useCallback(
    async (body: ChatStreamBody, callbacks: ChatStreamCallbacks): Promise<ChatStreamResult> => {
      controllerRef.current?.abort()
      const controller = new AbortController()
      controllerRef.current = controller
      stopReasonRef.current = null
      setIsStreaming(true)

      let text = ''
      let frame: number | null = null
      const flushText = () => {
        if (frame !== null) {
          cancelAnimationFrame(frame)
          frame = null
        }
        callbacks.onText(text)
      }

      try {
        if (!CHAT_STREAMING_ENABLED) {
          const response = await apiService.chatQuery(body)
          text = response.answer
          flushText()
          callbacks.onSources?.(response.sources ?? [])
          callbacks.onSuggestions?.(response.suggested_questions ?? [])
          callbacks.onDone?.(response.answer)

          return { status: 'done', text }
        }

        // Assigned inside the event callback, so keep TS from narrowing them to their initial values
        let failure = null as ChatStreamError | null
        let finished = false as boolean

        await apiService.streamChat(
          body,
          (event, data) => {
            switch (event) {
              case 'delta':
                text += data.text ?? ''
                if (frame === null) {
                  frame = requestAnimationFrame(() => {
                    frame = null
                    callbacks.onText(text)
                  })
                }
                break
              case 'sources':
                flushText()
                callbacks.onSources?.(data ?? [])
                break
              case 'suggestions':
                callbacks.onSuggestions?.(data ?? [])
                break
              case 'done':
                text = data.answer ?? text
                flushText()
                callbacks.onDone?.(text)
                finished = true
                break
              case 'error':
                failure = new ChatStreamError(200, data)
                break
            }
          },
          controller.signal
        )

        flushText()

        if (failure) return { status: 'error', text, error: failure }
        if (!finished)
          return {
            status: 'error',
            text,
            error: new ChatStreamError(0, { error: 'stream_incomplete' }),
          }

        return { status: 'done', text }
      } catch (err: any) {
        if (frame !== null) cancelAnimationFrame(frame)

        if (controller.signal.aborted && stopReasonRef.current) {
          if (stopReasonRef.current === 'stopped') callbacks.onText(text)
          return { status: stopReasonRef.current, text }
        }

        // Axios errors from the JSON fallback carry the response on err.response
        const error =
          err instanceof ChatStreamError
            ? err
            : new ChatStreamError(
                err?.response?.status ?? 0,
                err?.response?.data ?? { error: err?.message }
              )

        return { status: 'error', text, error }
      } finally {
        if (controllerRef.current === controller) {
          controllerRef.current = null
          setIsStreaming(false)
        }
      }
    },
    []
  )

  return { send, stop, abort, isStreaming }
}

/** True when the error is the chatbot's monthly quota (429 usage_limit_exceeded). */
export function isUsageLimitError(error: ChatStreamError): boolean {
  return error.status === 429 && error.data?.error === 'usage_limit_exceeded'
}
